import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/GridLegacy';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AppText } from '../../constant';
import { ApiManager } from '../../managers';
import { BASE_ID_SCHEMA_FIELD_ID, TABLE_ID_SCHEMA_FIELD_ID } from './Airtable.constants';
import { Container } from './Container';
import { AlertContext, EventSelector, FormInputType, FormInputTypeEnum, FormLoader, FormValidated, FormValidatedRef, schemaFieldToFormInput } from '@bit-by-bit-software/ui';
import { AirtableBases, AirtableIds, AirtableSchema, CookieValues, EventType, Model, ModelNameEnum, SendRequest } from '@bit-by-bit-software/types';
import ApiRequest from '../../api/request';
import { useCookies } from 'react-cookie';

function AirtableMapPage() {
  const createFormRef = useRef<FormValidatedRef>(null);

  const { showAlert } = useContext(AlertContext);

  const [cookies] = useCookies<'token', CookieValues>(['token']);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formInputs, setFormInputs] = useState<FormInputType[]>([]);
  const [, setRecords] = useState<AirtableIds[]>([]);
  const [activeRecordId, setActiveRecordId] = useState<string>('');
  const [event, setEvent] = useState<EventType>();
  const [airtableBaseId, setAirtableBaseId] = useState<string>('');
  const [airtableTableId, setAirtableTableId] = useState<string>('');
  const [schema, setSchema] = useState<AirtableSchema>();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const sendGetDataRequest: SendRequest<{params: { display?: boolean | undefined}, model: ModelNameEnum; }, { model: Model; results: any[] }> = ApiRequest.sendRequest;

    /**
   * Fetch the bases
   * Populate the base drop down
   * @returns the form inputs modified
   */
  const getAirtableBases = useCallback(async (formInputs: FormInputType[]) => {
    try {
      setIsLoading(true);

      const response = await ApiManager.SendRequest<void, AirtableBases>({
        route: 'airtable',
        action: 'bases',
        params: {},
      });

      if (response.results && !response.error) {
        // Convert table ID to drop down populated
        const newInputs = formInputs.map(input => {
          if (input.id === BASE_ID_SCHEMA_FIELD_ID) {
            input.type = FormInputTypeEnum.select;
            input.element = FormInputTypeEnum.select;
            input.options = response.results?.bases.bases.map(base => ({
              value: base.id,
              text: base.name,
            }));
          }
          return input;
        });
        return newInputs;
      }

      return formInputs;
    } catch (err: any) {
      return formInputs;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch the table schema any time we change the base
   */
  const getAirtableIds = useCallback(
    async (baseId: string, formInputs: FormInputType[]) => {
      try {
        setIsLoading(true);

        const response = await ApiManager.SendRequest<void, AirtableSchema>({
          route: 'airtable',
          action: 'schema',
          params: {
            baseId,
          },
        });

        if (response.results && !response.error) {
          setSchema(response.results);

          // Convert table ID to drop down populated
          const newFormInputs = formInputs.map(input => {
            if (input.id === TABLE_ID_SCHEMA_FIELD_ID) {
              input.type = FormInputTypeEnum.select;
              input.element = FormInputTypeEnum.select;
              input.options = response.results?.schema.tables.map(table => ({
                value: table.id,
                text: table.name,
              }));
            }
            return input;
          });

          setFormInputs(newFormInputs);
        }
      } catch (err: any) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
          variant: 'danger',
          dismissText: 'okay',
        });
      } finally {
        setIsLoading(false);
      }
    },       /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [showAlert],
  );
  
  const loadData = useCallback(
    async (eventId: string) => {
      try {
        // Load airtableids schema fields
        // Load airtableids records
        setIsLoading(true);

        const response = await ApiManager.SendRequest<{
          model: Model;
        }>({
          route: 'data',
          action: 'query',
          model: ModelNameEnum.airtableids,
          params: {
            event: eventId,
          },
        });
        if (response.error) {
          showAlert({
            headerText: 'Uh-oh',
            detailText: response.error,
            variant: 'danger',
            dismissText: 'okay',
          });
          return;
        }

        if (response.results.length) {
          setActiveRecordId(response.results[0]._id.toString());
        } else {
          setActiveRecordId('');
        }

        let createInputs: FormInputType[] = [];
        for (const schemaField of response.model.schemaFields) {
          if (!['isDeleted', 'createdBy', 'event'].includes(schemaField.name ?? '')) {
            const input = (await schemaFieldToFormInput(schemaField, sendGetDataRequest, cookies.token)) as FormInputType;
            if (input != null) {
              if (response.results.length) {
                const value = response.results[0][input.id];
                if (value) {
                  input.value = value;
                }
              }
              createInputs.push(input);
            }
          }
        }

        const baseIdIndex = createInputs.findIndex(input => input.id === BASE_ID_SCHEMA_FIELD_ID);
        const tableIdIndex = createInputs.findIndex(input => input.id === TABLE_ID_SCHEMA_FIELD_ID);

        const baseIdInput = createInputs.splice(baseIdIndex, 1)[0];
        const tableIdInput = createInputs.splice(
          tableIdIndex > baseIdIndex ? tableIdIndex - 1 : tableIdIndex,
          1,
        )[0];

        createInputs.unshift(tableIdInput);
        createInputs.unshift(baseIdInput);

        // @ts-ignore
        setRecords(response.results);

        createInputs = await getAirtableBases(createInputs);

        console.log(
          `baseIdInputvalue ${baseIdInput.value} tableIdInputValue ${tableIdInput.value}`,
        );
        if (baseIdInput.value && tableIdInput.value) {
          await getAirtableIds(baseIdInput.value, createInputs);
        } else {
          setFormInputs(createInputs);
        }
      } catch (err: any) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
          variant: 'danger',
          dismissText: 'okay',
        });
      } finally {
        setIsLoading(false);
      }
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    },       /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [showAlert, getAirtableBases, getAirtableIds, sendGetDataRequest],
  );

  const create = async () => {
    try {
      setIsLoading(true);

      if (!event) {
        throw new Error('Missing selected event');
      }

      const params: { [key: string]: string | number } = {
        event: event._id,
      };
      formInputs.forEach(({ id, value }) => {
        params[id] = value;
      });
      const response = await ApiManager.SendRequest({
        route: 'data',
        action: 'create',
        model: ModelNameEnum.airtableids,
        params,
      });
      if (response.error) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        return;
      }

      resetForm();

      setRecords(prev => [...prev, response.results]);

      showAlert({
        headerText: 'Success',
        detailText: 'Airtable ID map created',
        variant: 'success',
        dismissText: 'okay',
      });
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const update = async () => {
    try {
      setIsLoading(true);

      const params: { [key: string]: string | number } = {};
      formInputs.forEach(({ id, value }) => {
        params[id] = value;
      });

      const response = await ApiManager.SendRequest({
        route: 'data',
        action: 'update',
        model: ModelNameEnum.airtableids,
        id: activeRecordId,
        params,
      });
      if (response.error) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        return;
      }

      resetForm();
      setActiveRecordId('');
      setAirtableBaseId('');
      setAirtableTableId('');
      setEvent(undefined);

      setRecords(prev =>
        prev.map(p => {
          if (p._id === activeRecordId) {
            return response.results;
          }
          return p;
        }),
      );
      setActiveRecordId('');

      showAlert({
        headerText: 'Success',
        detailText: 'Airtable ID map updated',
        variant: 'success',
        dismissText: 'okay',
      });
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createFormOnChange = (change: any, isFormValid: boolean) => {
    setIsFormValid(isFormValid);
    const key = change.id.substr(change.id.lastIndexOf('.') + 1);

    const newFormInputs = [...formInputs];
    const createFormInput = newFormInputs.find(input => {
      if ([input.id, input.key].includes(key)) {
        return true;
      }
      return false;
    });

    if (createFormInput) {
      createFormInput.value = change.value;
    }
    setFormInputs(newFormInputs);
  };

  const resetForm = () => {
    const newCreateInputs = [];
    for (let i = 0; i < formInputs.length; i++) {
      const input = formInputs[i];
      input.value = '';
      newCreateInputs.push(input);
    }
    setFormInputs(newCreateInputs);
  };

  /**
   * Fetch schema when changing base
   * Populate drop down options when changing table
   */
  useEffect(() => {
    const baseId = formInputs.find(({ id }) => id === BASE_ID_SCHEMA_FIELD_ID)?.value;
    if (baseId && baseId !== airtableBaseId && baseId.length >= 17) {
      setAirtableBaseId(baseId);
      getAirtableIds(baseId, formInputs);
    }

    const tableId = formInputs.find(({ id }) => id === TABLE_ID_SCHEMA_FIELD_ID)?.value;
    if (tableId && tableId !== airtableTableId) {
      setAirtableTableId(tableId);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [formInputs]);

  /**
   * Set field ID's when changing table
   */
  useEffect(() => {
    if (schema) {
      const tableSchema = schema.schema.tables.find(table => table.id === airtableTableId);
      if (tableSchema) {
        const selectOptions = tableSchema.fields.map(({ id, name }) => ({
          value: id,
          text: name,
        }));
        setFormInputs(prevInputs =>
          prevInputs.map(input => {
            if (input.id !== TABLE_ID_SCHEMA_FIELD_ID && input.id !== BASE_ID_SCHEMA_FIELD_ID) {
              input.type = FormInputTypeEnum.select;
              input.element = FormInputTypeEnum.select;
              input.options = selectOptions;
            }
            return input;
          }),
        );
      }
    }
  }, [airtableTableId, schema]);

  /**
   * Fetch data from backend when changing event
   */
  useEffect(() => {
    if (event) {
      loadData(event._id.toString());
    }
  }, [event, loadData]);

  let inputsToRender = formInputs;
  if (!airtableBaseId) {
    inputsToRender = formInputs.filter(({ id }) => id === BASE_ID_SCHEMA_FIELD_ID);
  } else if (!airtableTableId) {
    inputsToRender = formInputs.filter(
      ({ id }) => id === TABLE_ID_SCHEMA_FIELD_ID || id === BASE_ID_SCHEMA_FIELD_ID,
    );
  }

  return (
    <Paper>
      <Container>
        <FormLoader isLoading={isLoading} />

        <DialogTitle>
          <Typography variant="h3" classes={{ h3: 'friends-title' }} display="block" align="center">
            {AppText.airtable.title}
          </Typography>
        </DialogTitle>
        <DialogContent className="friends-content">
          <FormControl component="fieldset">
            <FormGroup aria-label="notifications">
              <Grid container direction="row" style={{ marginTop: '25px', marginBottom: '25px' }}>
                <Grid item xs={12}>
                  <Typography
                    variant="h5"
                    classes={{ h5: 'preview-title' }}
                    display="block"
                    align="left">
                    {AppText.airtable.infoTitle}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" classes={{ body2: 'info-message' }} align="left">
                    {AppText.airtable.infoMessage}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" classes={{ body2: 'info-message' }} align="left">
                    {AppText.airtable.infoMessageRight}
                  </Typography>
                </Grid>
              </Grid>

              <EventSelector onChange={setEvent} selectedEventId={event?._id ?? ''} sendRequest={ApiRequest.sendRequest} />

              <div className="left-aligned create-form">
                {event && inputsToRender.length > 0 && (
                  <FormValidated
                    ref={createFormRef}
                    formInputs={inputsToRender}
                    formOnChange={createFormOnChange}
                    validateOnInit={false}
                    columns={2}
                    dynamicInputs
                    description=""
                  />
                )}
              </div>

              <Grid container direction="row" className="bottom-section">
                <Grid item xs={1} className="main-btn-section">
                  <Button
                    color="primary"
                    startIcon={<SaveIcon />}
                    disabled={!event || !isFormValid}
                    onClick={activeRecordId === '' ? create : update}>
                    Save
                  </Button>
                  <Button color="primary" startIcon={<ExitToAppIcon />} onClick={resetForm}>
                    Quit
                  </Button>
                </Grid>
              </Grid>
            </FormGroup>
          </FormControl>
        </DialogContent>
      </Container>
    </Paper>
  );
}

export default AirtableMapPage;
